import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SecurityFooter from '@/components/feature/SecurityFooter';
import BrandLogo from '@/components/feature/BrandLogo';
import IbanCard from './components/IbanCard';
import BalanceCard from './components/BalanceCard';
import AccountCard from './components/AccountCard';
import TransactionList, { type TransactionItem } from './components/TransactionList';
import SupportCard from './components/SupportCard';
import { getSessionClient, logoutClient, type ClientAccount } from '@/hooks/useClientAuth';
import { supabase } from '@/lib/supabase';

type TransactionRow = {
  id: string;
  type: string;
  label: string | null;
  counterparty: string;
  reference: string | null;
  amount: number | string;
  fee: number | string | null;
  currency: string | null;
  date: string;
  status: string;
};

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientAccount | null>(null);
  const [items, setItems] = useState<TransactionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const session = await getSessionClient();
        if (!session) {
          navigate('/');
          return;
        }
        if (!active) return;
        setClient(session);

        const { data } = await supabase
          .from('transactions')
          .select('*')
          .eq('client_id', session.id)
          .order('date', { ascending: false });

        if (!active) return;
        setItems(
          (data ?? []).map((row) => {
            const entry = row as TransactionRow;
            return {
              id: entry.id,
              type: entry.type,
              label: entry.label,
              counterparty: entry.counterparty,
              reference: entry.reference,
              amount: Number(entry.amount),
              fee: Number(entry.fee ?? 0),
              currency: entry.currency,
              date: entry.date,
              status: entry.status,
            };
          }),
        );
      } catch {
        if (active) setError(t('common.error'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate, t]);

  const handleLogout = () => {
    logoutClient();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 text-sm text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin text-lg"></i>
        {t('common.loading')}
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background-50 px-4 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-900">
          <i className="ri-error-warning-line text-xl"></i>
        </span>
        <p className="text-sm text-foreground-600">{error ?? t('common.loading')}</p>
        <a
          href="/"
          className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
        >
          {t('common.back')}
        </a>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <header className="sticky top-0 z-40 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[1280px] items-center justify-between gap-4 px-4 py-3.5 md:px-6">
          <a href="/dashboard" className="flex items-center gap-3">
            <BrandLogo className="h-14" />
          </a>

          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-3 sm:flex">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-semibold text-primary-800">
                {client.fullName.charAt(0)}
              </span>
              <span className="text-sm font-semibold text-foreground-800">{client.fullName}</span>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-logout-box-r-line"></i>
              <span className="hidden sm:inline">{t('common.logout')}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <div className="animate-fade-rise">
            <h1 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              {t('dashboard.welcome')}{' '}
              <span className="text-primary-700">{client.fullName}</span>
            </h1>
            <p className="mt-2 text-sm text-foreground-600">{t('brand.tagline')}</p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              <div className="relative animate-fade-rise">
                <IbanCard iban={client.iban} />
                <div className="relative z-10 -mt-3.5 flex justify-end pr-4">
                  <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900">
                    <i className="ri-shield-check-line text-sm"></i>
                    {t('dashboard.ibanSecure')}
                  </span>
                </div>
              </div>

              <BalanceCard
                balance={client.balance}
                currency={client.currency}
                onWithdraw={() => navigate('/withdrawal')}
              />
              <TransactionList items={items} currency={client.currency} />
            </div>

            <div className="space-y-6">
              <AccountCard accessCode={client.accessCode} currency={client.currency} />
              <SupportCard />
            </div>
          </div>
        </div>
      </main>

      <SecurityFooter />
    </div>
  );
}